/**
 * Your tickets (step 82) — the rules the screens keep, with no library in them.
 *
 * Reading and drawing a code needs zxing-wasm and pdf.js, which load only on
 * the tickets screens (`lib/lazy/ticketCodes.ts`, `lib/lazy/ticketPdf.ts`);
 * what is decided about a code — draw it or show the original, put the logo in
 * it, is the drawing the same code — lives here, so it is tested without them.
 */
import { TICKET_DRAWN_AS, isTicketFormat, ticketTakesLogo, type TicketDetails } from '@myrave/shared';
import { readJson, writeJson } from '@/lib/storage';

/** A ticket as the API answers it (`backend/src/modules/raveTickets/service.ts` `ticketsOf`). */
export interface Ticket {
  id: string;
  editionId: string;
  originalId: string;
  fileType: string;
  page: number | null;
  name: string;
  description: string | null;
  format: string;
  binary: boolean;
  /** The code's text — or, when `binary`, its bytes in base64. */
  value: string;
  /** What is printed on it (decision O2): holder, ID number, ticket and order number, type, price, purchase date, other. */
  details?: TicketDetails | null;
  createdAt: string;
}

/**
 * MyRave's drawing design: a stored ticket drawn under an older one is drawn again on the next
 * sync (2 = the user's "A3", 2026-10-28). Bump it when `qrDesign.ts` changes what is drawn.
 */
export const TICKET_DRAWING_VERSION = 2;

/** What a kept drawing was made from: the design's version and the rave's logo it carries. */
export function drawKey(logoUrl: string | null | undefined): string {
  return `${TICKET_DRAWING_VERSION}|${logoUrl ?? ''}`;
}

/** A rave in `GET /me/tickets`. */
export interface TicketRave {
  id: string;
  name: string;
  nameLocal: Record<string, string> | null;
  festival: { name: string; nameLocal: Record<string, string> | null; subdomain: string };
  timezone: string;
  /** Where it is (the wallet's "dates · place" line); absent from a copy kept before it was sent. */
  venue?: string | null;
  gatesOpenAt: string | null;
  endsAt: string | null;
}

/** The format to draw a read code in, or null — shown as the original (decision C). */
export function drawFormat(format: string): string | null {
  return isTicketFormat(format) ? TICKET_DRAWN_AS[format] ?? null : null;
}

/** QR gets the MyRave logo in the middle (Aztec cannot — its middle is its finder); every other code is drawn as it is. */
export function drawsWithLogo(format: string): boolean {
  return drawFormat(format) !== null && ticketTakesLogo(format);
}

/** What one reading of a code says — the original's, or MyRave's drawing read back. */
export interface CodeRead {
  format: string;
  text: string;
  bytes?: Uint8Array;
  binary: boolean;
  /** The symbology identifier (`]Q1`, `]C1` …) — says GS1 / ECI, which the text alone does not. */
  symbology?: string;
}

/** The same family under the reader's names: Aztec ↔ AztecCode, QR model 2 ↔ QR. */
function family(format: string): string {
  if (format === 'AztecCode') return 'Aztec';
  if (format === 'QRCodeModel2') return 'QRCode';
  if (format === 'UPCA' || format === 'EAN13') return 'EAN13';
  return format;
}

const RETAIL = new Set(['EAN13', 'UPCA']);

/**
 * Does MyRave's drawing hold the SAME code as the original? (step 82: "reads
 * its own drawing back"). The same text — a UPC-A reads back as EAN-13 with a
 * leading 0 — or, for a code that holds bytes, the same bytes; and the same
 * symbology identifier where both are known, so a GS1 code drawn as plain text
 * (which a gate would read differently) fails.
 */
export function sameCode(original: CodeRead, drawn: CodeRead): boolean {
  if (family(original.format) !== family(drawn.format)) return false;
  if (original.binary || drawn.binary) {
    const a = original.bytes;
    const b = drawn.bytes;
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  const retail = RETAIL.has(original.format) || RETAIL.has(drawn.format);
  if (original.text !== drawn.text) {
    if (!retail) return false;
    const strip = (s: string) => (s.length === 13 && s.startsWith('0') ? s.slice(1) : s);
    if (strip(original.text) !== strip(drawn.text)) return false;
  }
  if (!retail && original.symbology && drawn.symbology && original.symbology !== drawn.symbology) return false;
  return true;
}

/** Base64 of bytes, and back — a code that holds bytes is kept as base64 (`binary`). */
export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
export function fromBase64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/* ---------------------------------------------------- the gate button --- */

/**
 * Festival mode's Ticket button, per rave, on the phone (`myrave:{editionId}:tickets`, step 82 N5):
 * how many tickets the phone holds for it, and whether the button is gone for good —
 * `insideAt` (the phone was once near a stage, N2) or `hiddenAt` ("I'm in — hide this", N3).
 */
export interface TicketBarState {
  count: number;
  insideAt?: number | null;
  hiddenAt?: number | null;
}
const KEY = 'tickets';
const listeners = new Set<() => void>();

export function readTicketBar(editionId: string): TicketBarState {
  return readJson<TicketBarState>(editionId, KEY, { count: 0 });
}
function writeTicketBar(editionId: string, next: TicketBarState): void {
  writeJson(editionId, KEY, next);
  for (const cb of listeners) cb();
}
export function onTicketBar(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
/** How many tickets this phone holds for the rave — written whenever the device copy changes. */
export function noteTicketCount(editionId: string, count: number): void {
  const now = readTicketBar(editionId);
  if (now.count !== count) writeTicketBar(editionId, { ...now, count });
}
/** "I'm in — hide this" (N3): the button is gone for good for this rave. */
export function hideTicketButton(editionId: string, at = Date.now()): void {
  const now = readTicketBar(editionId);
  if (!now.hiddenAt) writeTicketBar(editionId, { ...now, hiddenAt: at });
}

/**
 * Whether the bar shows the Ticket button now (N1–N4, the user 2026-10-28): all the time while
 * the phone holds a ticket for the rave — before it too ("not only the first day") — until it
 * ends, and never again once a stage was reached or the raver hid it.
 */
export function ticketButtonShows(
  state: TicketBarState,
  edition: { gatesOpenAt?: string | null; endsAt?: string | null },
  _timezone: string,
  now: Date,
): boolean {
  if (state.count < 1 || state.insideAt || state.hiddenAt) return false;
  // All the time BEFORE the rave too, not only from the gates' day (the user, 2026-10-28) —
  // until a stage is reached, "I'm in", or the rave ends.
  if (!edition.endsAt) return true;
  const ends = new Date(edition.endsAt);
  return Number.isNaN(ends.getTime()) || now < ends;
}

/**
 * A fix near a stage (step 60's watcher): if the Ticket button is still showing, the raver is
 * inside — hide it for good (N2). The position itself is never kept or sent; only the moment.
 * True when this call hid it.
 */
export function noteNearStageForTickets(
  editionId: string,
  edition: { gatesOpenAt?: string | null; endsAt?: string | null },
  timezone: string,
  now = new Date(),
): boolean {
  const state = readTicketBar(editionId);
  if (!ticketButtonShows(state, edition, timezone, now)) return false;
  writeTicketBar(editionId, { ...state, insideAt: now.getTime() });
  return true;
}

/**
 * When a rave of My tickets is, against `now` (QA w-tickets-account, the user, 2026-11-02: "in all my
 * tickets pages, sort the tickets by rave dates and show a counter or live now on each rave"): `live`
 * from the gates to the end; `soon` before the gates, with what is left; `ended` after; null with no
 * dates. A rave with no end is over a day after its gates.
 */
export type RaveWhen =
  | { kind: 'live' }
  | { kind: 'soon'; days: number; hours: number; minutes: number }
  | { kind: 'ended' };

export function raveWhen(r: Pick<TicketRave, 'gatesOpenAt' | 'endsAt'>, now: number): RaveWhen | null {
  if (!r.gatesOpenAt) return null;
  const gates = Date.parse(r.gatesOpenAt);
  const ends = r.endsAt ? Date.parse(r.endsAt) : gates + 86_400_000;
  if (Number.isNaN(gates)) return null;
  if (now >= ends) return { kind: 'ended' };
  if (now >= gates) return { kind: 'live' };
  const minutesLeft = Math.max(1, Math.ceil((gates - now) / 60_000));
  return { kind: 'soon', days: Math.floor(minutesLeft / 1440), hours: Math.floor((minutesLeft % 1440) / 60), minutes: minutesLeft % 60 };
}

/**
 * My tickets' raves by their dates: those not over first — on now and the soonest gates leading — then
 * the ended ones, the most recent first; a rave with no dates last.
 */
export function byRaveDates(now: number) {
  return (a: Pick<TicketRave, 'gatesOpenAt' | 'endsAt'>, b: Pick<TicketRave, 'gatesOpenAt' | 'endsAt'>): number => {
    const rank = (r: Pick<TicketRave, 'gatesOpenAt' | 'endsAt'>) => {
      const w = raveWhen(r, now);
      return w === null ? 2 : w.kind === 'ended' ? 1 : 0;
    };
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 2) return 0;
    const ga = Date.parse(a.gatesOpenAt!);
    const gb = Date.parse(b.gatesOpenAt!);
    return ra === 1 ? gb - ga : ga - gb;
  };
}
